import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axiosClient from '../../api/axiosClient';
import styled from 'styled-components';
import Button from '../../components/Button/Button';
import Typography from '../../components/Typography';
import { theme } from '../../theme';
import { fetchWithTimeout, geocodeCity, reverseGeocode, getCoordsFromBrowser } from '../../utils/location';
import HelmetIcon from '../../components/Icons/HelmetIcon';
import GarageOutlineIcon from '../../components/Icons/GarageOutlineIcon'
import HeartOutlineIcon from '../../components/Icons/HeartOutlineIcon';

// Importar los componentes de pasos
import PersonalDetailsStep from './PersonalDetailsStep';
import InterestsStep from './InterestsStep';
import GarageStep from './GarageStep';

const CompleteProfile = () => {
  const { t } = useTranslation('completeProfile');
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshUserData } = useAuth();

  // Estado para manejar los pasos
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [isResolvingCity, setIsResolvingCity] = useState(false);
  const [postStep1PromptVisible, setPostStep1PromptVisible] = useState(false);

  // Estados para los datos del formulario
  const [formData, setFormData] = useState({
    name: '',
    lastName: '',
    userAvatar: '',
    description: '',
    address: '',
    locality: '',
    country: '',
    phonePrefix: '+34',
    phoneNumber: '',
    socialMediaLinks: []
  });

  const [interests, setInterests] = useState({
    eventTypes: [],
    terrains: [],
    experiences: [],
    vehicleTypes: [],
    eventFrequency: '',
    organizingFrequency: '',
    professionalOrganizer: '',
    eventSuggestions: false,
    nearbyCity: ''
  });
  const [file, setFile] = useState(null);
  const [pendingLocationCoordinates, setPendingLocationCoordinates] = useState(null);

  // Cargar datos del usuario al montar el componente
  useEffect(() => {
    if (user) {
      const newFormData = {
        name: user.name || '',
        lastName: user.lastName || '',
        userAvatar: user.userAvatar || '',
        description: user.description || '',
        address: user.address || '',
        locality: user.locality || '',
        country: user.country || '',
        phonePrefix: user.phonePrefix || '+34',
        phoneNumber: user.phoneNumber || '',
        socialMediaLinks: user.socialMediaLinks || []
      };
      setFormData(newFormData);

      const newInterests = {
        eventTypes: user.eventTypeInterests || [],
        terrains: user.terrainInterests || [],
        experiences: user.experienceInterests || [],
        vehicleTypes: user.vehicleTypeInterests || [],
        eventFrequency: user.eventFrequency || '',
        organizingFrequency: user.organizingFrequency || '',
        professionalOrganizer: user.professionalOrganizer || '',
        eventSuggestions: user.profileFilled && user.receiveEventSuggestions !== undefined ? user.receiveEventSuggestions : false,
        nearbyCity: user.nearbyCity || ''
      };
      setInterests(newInterests);

      // Marcar pasos como completados si ya tienen datos
      const newCompletedSteps = new Set();

      // Paso 1: Información personal - marcar como completado si tiene nombre y apellido
      if (user.name && user.lastName) {
        newCompletedSteps.add(1);
      }

      // Paso 2: Intereses - marcar como completado si tiene al menos un interés configurado
      if (user.eventTypeInterests?.length > 0 || user.terrainInterests?.length > 0 ||
        user.experienceInterests?.length > 0 || user.vehicleTypeInterests?.length > 0 ||
        user.eventFrequency || user.organizingFrequency || user.professionalOrganizer) {
        newCompletedSteps.add(2);
      }

      // Paso 3: Garaje - marcar como completado si tiene vehículos
      if (user.vehicles && user.vehicles.length > 0) {
        newCompletedSteps.add(3);
      }

      setCompletedSteps(newCompletedSteps);
    }
  }, [user]);

  // Funciones para navegación entre pasos
  const nextStep = () => {
    if (currentStep < 3) {
      // Marcar el paso actual como completado antes de avanzar
      setCompletedSteps(prev => {
        const newSet = new Set(prev);
        newSet.add(currentStep);
        return newSet;
      });
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (currentStep > 2) {
      setCurrentStep(currentStep - 1);
    }
  };

  const skipStep = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (currentStep === 2 || currentStep === 3) {
      setCompletedSteps(prev => new Set([...prev, currentStep]));
      if (currentStep === 2) {
        setCurrentStep(3);
      } else {
        handleFinalSubmit();
      }
    }
  };

  // Validación específica por paso
  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.name.trim() !== '' && formData.lastName.trim() !== '';
      case 2:
        return true; // Los intereses son opcionales
      case 3:
        return true; // El vehículo es opcional
      default:
        return false;
    }
  };

  // Guardar información personal (Paso 1)
  const handlePersonalInfoSubmit = async () => {
    setIsLoading(true);

    try {
      const data = new FormData();
      data.append('name', formData.name);
      data.append('lastName', formData.lastName);
      data.append('description', formData.description);
      // Dirección no requerida
      data.append('phonePrefix', formData.phonePrefix);
      data.append('phoneNumber', formData.phoneNumber);
      data.append('socialMediaLinks', JSON.stringify(formData.socialMediaLinks));
      if (file) data.append('userAvatar', file);



      const response = await axiosClient.put('/api/users/profile', data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        await refreshUserData();
        setPostStep1PromptVisible(true);
      }
    } catch (error) {
      console.error('Error updating personal info:', error);
      // toast.error(error.response?.data?.message || t('profile.updateError'));
    } finally {
      setIsLoading(false);
    }
  };

  const continueToBlock2 = () => {
    setPostStep1PromptVisible(false);
    setCurrentStep(2);
  };

  const finishAfterBlock1 = () => {
    handleFinalSubmit();
  };

  // Guardar intereses (Paso 2)
  const handleInterestsSubmit = async () => {
    if (isLoading) return; // Prevenir múltiples ejecuciones
    setIsLoading(true);

    try {
      if (interests.eventSuggestions && (!interests.nearbyCity || !interests.nearbyCity.trim())) {
        toast.error(t('interests.nearbyCityRequired'));
        setIsLoading(false);
        return;
      }
      const data = new FormData();

      // Solo enviar datos de intereses en este paso
      // Los datos personales ya fueron guardados en el paso anterior

      // Incluir datos de intereses
      data.append('eventTypeInterests', JSON.stringify(interests.eventTypes));
      data.append('terrainInterests', JSON.stringify(interests.terrains));
      data.append('experienceInterests', JSON.stringify(interests.experiences));
      data.append('vehicleTypeInterests', JSON.stringify(interests.vehicleTypes));
      data.append('eventFrequency', interests.eventFrequency);
      data.append('organizingFrequency', interests.organizingFrequency);
      data.append('professionalOrganizer', interests.professionalOrganizer);
      data.append('receiveEventSuggestions', interests.eventSuggestions);
      if (interests.eventSuggestions) {
        data.append('nearbyCity', interests.nearbyCity);
        data.append('notifyOnNearbyEvent', true);
        const radius = (user?.nearbyRadiusKm && Number(user.nearbyRadiusKm) > 0) ? Number(user.nearbyRadiusKm) : 25;
        data.append('nearbyRadiusKm', radius);
        if (pendingLocationCoordinates && pendingLocationCoordinates.coordinates && pendingLocationCoordinates.coordinates.length === 2) {
          data.append('locationCoordinates', JSON.stringify(pendingLocationCoordinates));
        } else if (String(interests.nearbyCity || '').trim()) {
          try {
            const coords = await geocodeCity(interests.nearbyCity, 'es');
            if (coords) {
              data.append('locationCoordinates', JSON.stringify(coords));
            }
          } catch { }
        }
      } else {
        data.append('notifyOnNearbyEvent', false);
      }

      if (file) data.append('userAvatar', file);



      const response = await axiosClient.put('/api/users/profile', data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        await refreshUserData();
        // toast.success(t('profile.interestsSaved'));
        nextStep();
      }
    } catch (error) {
      console.error('Error updating interests:', error);
      // toast.error(error.response?.data?.message || t('profile.updateError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleUseDeviceLocation = async () => {
    if (isLocating) return
    try {
      setIsLocating(true)
      const pos = await getCoordsFromBrowser()
      const { latitude, longitude } = pos.coords
      const coords = { type: 'Point', coordinates: [Number(longitude), Number(latitude)] }
      setPendingLocationCoordinates(coords)
      try {
        const cityName = await reverseGeocode(latitude, longitude, 'es')
        if (cityName) setInterests(prev => ({ ...prev, nearbyCity: cityName }))
      } catch { }
    } catch (e) {
      toast.error(t('interests.messages.error'))
    } finally {
      setIsLocating(false)
    }
  }

  // Finalizar proceso completo
  const handleFinalSubmit = async () => {
    setIsLoading(true);

    try {
      const data = new FormData();

      // Solo enviar profileFilled para marcar el perfil como completado
      // Los datos personales ya fueron guardados en el paso 1
      data.append('profileFilled', true);

      const response = await axiosClient.put('/api/users/profile', data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        const updatedUser = await refreshUserData();

        if (updatedUser && updatedUser.profileFilled) {
          toast.success(t('toasts.profileCompleted'));
          const destination = location.state?.returnTo || '/discover';
          navigate(destination, { replace: true });
        } else {
          toast.error(t('toasts.updateUserError'));
        }
      }
    } catch (error) {
      console.error('Error completing profile:', error);
      if (error?.response?.status === 401) {
        toast.error(t('toasts.sessionExpired'));
        navigate('/signin');
      } else {
        toast.error(t('toasts.updateProfileError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Manejar envío de cada paso
  const handleStepSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep(currentStep)) {
      toast.error(t('profile.requiredFields'));
      return;
    }

    if (currentStep === 1) {
      await handlePersonalInfoSubmit();
    } else if (currentStep === 2) {
      await handleInterestsSubmit();
    } else if (currentStep === 3) {
      handleFinalSubmit();
    }
  };

  // Renderizar contenido del paso actual
  const renderStepContent = () => {
    if (postStep1PromptVisible) {
      return (
        <ProfileSkipBanner>
          <VisualBlock>
            <VisualDecoration>
              <Bubble>
                {/* Intereses */}
                <HeartOutlineIcon width={20} height={20} fill={theme.fill.brandMain} />
              </Bubble>
              <Connector>

              </Connector>
            </VisualDecoration>
            <AvatarWrapper>
              {user?.userAvatar ? (
                <AvatarImage src={user.userAvatar} alt="user avatar" />
              ) : (
                <AvatarPlaceholder>
                  <HelmetIcon width={28} height={28} fill={theme.colors.defaultMain} />
                </AvatarPlaceholder>
              )}
            </AvatarWrapper>
            <VisualDecoration>
              <Connector>
              </Connector>
              <Bubble>
                {/* Garaje */}
                <GarageOutlineIcon width={20} height={20} fill={theme.fill.brandMain} />
              </Bubble>
            </VisualDecoration>
          </VisualBlock>
          <Typography as="h3" $variant="title-5-semibold" color={theme.colors.defaultStrong}>
            {t('profile.block2InvitationTitle', { defaultValue: '¿Quieres completar tus intereses y garaje ahora?' })}
          </Typography>
          <Typography as="p" $variant="body-1-regular" color={theme.colors.defaultWeak}>
            {t('profile.block2InvitationSubtitle', { defaultValue: 'Puedes hacerlo ahora o saltarlo por ahora. Siempre podrás completarlo más tarde desde tu perfil.' })}
          </Typography>
          <ProfileSkipActions>
            <Button
              type="button"
              $variant="outline"
              onClick={finishAfterBlock1}
              size="l"
            >
              {t('common.skip')}
            </Button>
            <Button
              type="button"
              $variant=""
              onClick={continueToBlock2}
              size="l"
            >
              {t('common.continue')}
            </Button>

          </ProfileSkipActions>
        </ProfileSkipBanner>
      );
    }
    switch (currentStep) {
      case 1:
        return (
          <PersonalDetailsStep
            key={`personal-details-${currentStep}`}
            formData={formData}
            setFormData={setFormData}
            file={file}
            setFile={setFile}
          />
        );
      case 2:
        return (
          <InterestsStep
            interests={interests}
            setInterests={setInterests}
            onUseDeviceLocation={handleUseDeviceLocation}
            isLocating={isLocating}
          />
        );
      case 3:
        return (
          <GarageStep
            onComplete={handleFinalSubmit}
          />
        );
      default:
        return null;
    }
  };

  // Renderizar acciones específicas para cada paso
  const renderStepActions = () => {
    if (postStep1PromptVisible) {
      return null;
    }
    switch (currentStep) {
      case 1:
        return (
          <FormActions>
            <FormActionsContent $twoColumns={false}>
              <Button
                type="submit"
                size="l"
                disabled={isLoading || !validateStep(currentStep)}
                $variant="primary"
              >
                {isLoading ? t('common.saving') : t('common.continue')}
              </Button>
            </FormActionsContent>
          </FormActions>
        );

      case 2:
        return (
          <FormActions>
            <FormActionsContent $twoColumns={false}>
              <div style={{ display: 'flex', gap: '12px' }}>
                <Button
                  type="button"
                  size="l"
                  $variant="ghost"
                  onClick={skipStep}
                >
                  {t('common.skip')}
                </Button>

                <Button
                  type="submit"
                  size="l"
                  disabled={isLoading || !validateStep(currentStep)}
                  $variant="primary"
                >
                  {isLoading ? t('common.saving') : t('common.continue')}
                </Button>
              </div>
            </FormActionsContent>
          </FormActions>
        );

      case 3:
        return (
          <FormActions>
            <FormActionsContent $twoColumns={true}>
              <Button
                type="button"
                $variant="outline"
                onClick={prevStep}
                size="l"
              >
                {t('common.previous')}
              </Button>

              <div style={{ display: 'flex', gap: '12px' }}>
                <Button
                  type="button"
                  $variant="ghost"
                  onClick={skipStep}
                  size="l"
                >
                  {t('common.skip')}
                </Button>

                <Button
                  type="submit"
                  disabled={isLoading || !validateStep(currentStep)}
                  $variant="success"
                  size="l"
                >
                  {isLoading ? t('common.saving') : t('common.finish')}
                </Button>
              </div>
            </FormActionsContent>
          </FormActions>
        );

      default:
        return null;
    }
  };

  const renderNavigation = () => {
    if (postStep1PromptVisible) {
      return (
        <Navigation>
          <NavbarContainer>
            <Typography as="h1" $variant="title-5-semibold" $variantTablet="body-1-semibold" $variantMobile="body-2-semibold">
              {t('pageTitle.complete')}
            </Typography>
          </NavbarContainer>
        </Navigation>
      );
    }
    if (currentStep === 1) {
      return (
        <Navigation>
          <NavbarContainer>
            <Typography as="h1" $variant="title-5-semibold" $variantTablet="body-1-semibold" $variantMobile="body-2-semibold">
              {t('tabs.addUserInfo')}
            </Typography>
          </NavbarContainer>
        </Navigation>
      );
    }
    return (
      <Navigation>
        <NavbarContainer>
          <Typography as="h1" $variant="title-5-semibold" $variantTablet="body-1-semibold" $variantMobile="body-2-semibold">
            {currentStep === 2 ? t('tabs.markInterests') : t('tabs.addGarage')}
          </Typography>
          <Typography $variant="body-2-regular" color={theme.colors.defaultWeak}>
            {t('profile.step')} {currentStep - 1} {t('profile.of')} 2
          </Typography>
        </NavbarContainer>
      </Navigation>
    );
  };

  return (
    <CompleteProfileForm onSubmit={handleStepSubmit}>
      {renderNavigation()}

      {/* Contenido del formulario */}
      <FormContainer>
        <FormContent>
          {renderStepContent()}
        </FormContent>

        {/* Acciones del formulario */}
        {renderStepActions()}
      </FormContainer>
    </CompleteProfileForm>
  );
};

export default CompleteProfile;

// Styled Components
const CompleteProfileForm = styled.form`
  align-items: center;
  background: ${({ theme }) => theme.fill.defaultSubtle};
  display: flex;
  flex-direction: column;
  padding-bottom: 120px;
  position: relative;
  width: 100%;
  max-width: 100%;
  min-height: 100vh;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    background: ${({ theme }) => theme.fill.defaultMain};
  }
`;

const Navigation = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  width: 100%;
  z-index: 1;
`;

const NavbarContainer = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: space-between;
  max-width: 1400px;
  padding: ${({ theme }) => theme.sizing.sm} ${({ theme }) => theme.sizing.md};
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.sizing.sm};
  }
`;

const FormContainer = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.fill.defaultMain};
  border: 1px solid ${({ theme }) => theme.border.defaultWeak};
  border-radius: ${({ theme }) => theme.sizing.sm};
  box-shadow: 0 2px 12px 0 rgb(26 26 26 / 4%);
  display: flex;
  flex-direction: column;
  // margin-top: 80px;
  // margin-top: 32px;
  max-width: 600px;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    border: none;
    border-radius: 0;
    box-shadow: none;
    // margin-top: 56px;
    max-width: 100%;
  }
`;

const FormContent = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.md};
  padding: ${({ theme }) => theme.sizing.md};
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.sizing.sm};
  }
`;

const FormActions = styled.div`
  border-top: 1px solid ${({ theme }) => theme.border.defaultWeak};
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.sm};
  padding: ${({ theme }) => theme.sizing.sm} ${({ theme }) => theme.sizing.md};
  width: 100%;

  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  background: ${({ theme }) => theme.fill.defaultMain};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.sizing.sm};
  }
`;

const FormActionsContent = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.sm};
  justify-content: ${({ $twoColumns }) => $twoColumns ? 'space-between' : 'flex-end'};
  width: 100%;
  // max-width: 600px;
  margin: 0 auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    max-width: 100%;
  }
`;

const ProfileSkipBanner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.sizing.sm};
    border: 1px solid ${({ theme }) => theme.border.defaultWeak};
    border-radius: ${({ theme }) => theme.sizing.sm};
    box-shadow: 0 2px 12px 0 rgb(26 26 26 / 4%);
  }
`;

const ProfileSkipActions = styled.div`
  padding-top: ${({ theme }) => theme.sizing.xs};
  display: flex;
  flex-direction: row;
  gap: 12px;
  justify-content: space-between;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column-reverse;
  }
`;

const VisualBlock = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.sizing.md};
  background: ${({ theme }) => theme.fill.brandAlphaMain8};
  border-radius: ${({ theme }) => theme.sizing.xs};
  margin-bottom: ${({ theme }) => theme.sizing.xs};
`;

const VisualDecoration = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const Bubble = styled.div`
  height: 40px;
  width: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.fill.brandAlphaMain24};
  // background-color: ${({ theme }) => theme.fill.brandAlphaMain8};
  border-radius: ${({ theme }) => theme.radius.xl};
`;

const Connector = styled.div`
  height: 1px;
  width: 32px;
  background-color: ${({ theme }) => theme.fill.brandAlphaMain24};
`;

const AvatarWrapper = styled.div`
  display: flex;
  padding: ${({ theme }) => theme.sizing.xs};
  border: 1px solid ${({ theme }) => theme.fill.brandAlphaMain24};
  border-radius: ${({ theme }) => theme.radius.xl};
`;

const AvatarImage = styled.img`
  width: 56px;
  height: 56px;
  border-radius: ${({ theme }) => theme.radius.xl};
  object-fit: cover;
`;

const AvatarPlaceholder = styled.div`
  width: 56px;
  height: 56px;
  border-radius: ${({ theme }) => theme.radius.xl};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.fill.defaultMain};
  // border: 2px solid ${({ theme }) => theme.border.defaultMain};
  box-shadow: 0px 0px 5px ${({ theme }) => theme.fill.inverseAlphaMain8}, 0px 5px 16px ${({ theme }) => theme.fill.inverseAlphaMain8};
`;
